import React from 'react';
import { StringGroup } from '../types';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';
import {
  getClientDisplayName,
  isLangHubClient,
} from 'src/features/string-group-detail/utils/clientIdMapper';
import ExpandableCell from 'src/features/namespace/components/ExpandableCell';
import DetailsPanel, { DetailsRow } from 'src/common/ui/DetailsPanel';
import { useTranslation, type TranslateFn } from 'src/i18n/shim/useTranslation';

interface MetadataPanelProps {
  group: StringGroup;
}

const NOT_AVAILABLE = '—';

/**
 * Formats an epoch-millis timestamp as "DD Mon YYYY". `useStringGroupData`
 * defaults missing server timestamps to 0, which would otherwise render as
 * 01 Jan 1970.
 */
function formatDate(locale: string, timestamp: number): string {
  if (!timestamp) {
    return NOT_AVAILABLE;
  }
  return formatDayMonthYear(locale, new Date(timestamp));
}

/** Renders "<date> by <user>", omitting either part when it is unavailable. */
function formatDateWithActor(
  t: TranslateFn,
  locale: string,
  timestamp: number,
  actor: string,
): string {
  const date = formatDate(locale, timestamp);
  if (!actor) {
    return date;
  }
  return date === NOT_AVAILABLE
    ? actor
    : t('namespace-metadata-date-by-author', { date, author: actor });
}

/**
 * "Details" panel for the string group detail page. Maps the string group's
 * metadata to rows and renders them through the shared {@link DetailsPanel},
 * which the string detail page reuses too.
 */
const MetadataPanel: React.FC<MetadataPanelProps> = ({ group }) => {
  const { t, locale } = useTranslation();
  const rows: DetailsRow[] = [
    { label: t('string-meta-cx-family'), value: group.cxFamilyId },
    {
      label: t('string-meta-cx-ids'),
      value: <ExpandableCell items={group.cxIds} maxVisible={3} />,
      rich: true,
    },
    // Source locale is a group-level property only for LangHub-owned groups.
    // Groups owned by other clients (e.g. TranscomFileUI/AST) have no single
    // group-level source locale — DescribeStringGroup returns null — because
    // their strings can each carry a different one, so the locale is shown
    // per string in the strings table instead and this row is omitted.
    ...(isLangHubClient(group.clientId)
      ? [
          {
            label: t('sgd-field-source-locale'),
            value: group.sourceLocale,
            testId: 'group-source-locale',
          },
        ]
      : []),
    { label: t('sgd-field-team-name'), value: group.teamName ?? NOT_AVAILABLE },
    {
      label: t('sgd-metadata-source'),
      value: getClientDisplayName(group.clientId),
    },
    {
      label: t('sgd-metadata-created-on'),
      value: formatDateWithActor(t, locale, group.createdOn, group.createdBy),
    },
    {
      label: t('sgd-metadata-submitted-on'),
      value: formatDateWithActor(t, locale, group.updatedOn, group.updatedBy),
    },
    // Only shown for a version whose translation was cancelled — omitted
    // entirely otherwise rather than rendering a "—" row for every group.
    ...(group.cancelRequestedOn
      ? [
          {
            label: t('sgd-metadata-cancel-requested-on'),
            value: formatDateWithActor(
              t,
              locale,
              group.cancelRequestedOn,
              group.cancelRequestedBy ?? '',
            ),
          },
        ]
      : []),
    {
      label: t('sgd-metadata-trace-id'),
      value: group.traceId ?? NOT_AVAILABLE,
    },
  ];

  return <DetailsPanel rows={rows} />;
};

export default MetadataPanel;
